import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { getPartImages, getRenderKey } from "../../helpers/conservatoryImage";
import { BFC_FIXED_CHROME_HEIGHT_MOBILE } from "../../constants/constants";

import ConservatoryImage from "../ConservatoryImage/ConservatoryImage";
import SvgComponent from "../SvgComponent/SvgComponent";
import { Glyphicon } from "../Glyphicon/Glyphicon";
import { PreviewLightbox } from "../PreviewLightbox/PreviewLightbox";
import { usePreviewData } from "../Preview/usePreviewData";

import { useIsScrolledPast, useHasRenderChanged } from "./hooks";

import {
  _Bar as Bar,
  _ThumbButton as ThumbButton,
  _ZoomBadge as ZoomBadge,
  _Details as Details,
  _NameRow as NameRow,
  _Season as Season,
  _SizeRow as SizeRow,
  _Dimensions as Dimensions,
  _Price as Price,
  _OrdinaryPrice as OrdinaryPrice,
} from "./style";

/* Collapsed summary that takes over once the expanded preview has scrolled
away. Mobile only; on desktop the preview stays beside the options. */
const PreviewBar = ({ previewRef, renderDimensions = false, dataId }) => {
  const { assetBaseUrl } = useSelector((state) => state.init);
  const Uterum = useSelector((state) => state.Uterum);
  const { Images } = useSelector((state) => state.Images);

  const [lightboxIsOpen, setLightboxIsOpen] = useState(false);

  const isVisible = useIsScrolledPast(previewRef, BFC_FIXED_CHROME_HEIGHT_MOBILE);

  const renderKey = Images ? getRenderKey(getPartImages(Images, assetBaseUrl, Uterum)) : "";
  const [isPulsing, clearPulse] = useHasRenderChanged(renderKey, isVisible);

  const {
    alias,
    frameType,
    frameTypeTitle,
    seasonTitle,
    seasonIconName,
    squareMeter,
    frameWidth,
    frameDepth,
    formattedPrice,
    formattedOrdinaryPrice,
    hasAnyDiscount,
  } = usePreviewData();

  const smaller = Uterum.Ground ? true : false;

  return (
    <>
      <Bar $visible={isVisible} data-id={dataId} aria-hidden={!isVisible}>
        <ThumbButton
          $pulsing={isPulsing}
          onAnimationEnd={clearPulse}
          onClick={() => setLightboxIsOpen(true)}
          aria-label={alias.bfcZoomImage}
          data-id={`${dataId}__Thumb`}
        >
          <ConservatoryImage
            variant="thumb"
            frameType={frameType}
            smaller={smaller}
          />
          <ZoomBadge>
            <Glyphicon name="zoom-in" />
          </ZoomBadge>
        </ThumbButton>

        <Details>
          <NameRow>
            <span>{frameTypeTitle}</span>
            <Season>
              <SvgComponent name={seasonIconName} />
              <span>{seasonTitle}</span>
            </Season>
          </NameRow>
          <SizeRow>
            {squareMeter} m<sup>2</sup>
            {renderDimensions && (
              <Dimensions>
                ({frameWidth}x{frameDepth})
              </Dimensions>
            )}
          </SizeRow>
        </Details>

        <Price $discounted={hasAnyDiscount}>
          {formattedPrice}
          {hasAnyDiscount && (
            <OrdinaryPrice>{formattedOrdinaryPrice}</OrdinaryPrice>
          )}
        </Price>
      </Bar>

      <PreviewLightbox
        isOpen={lightboxIsOpen}
        onClose={() => setLightboxIsOpen(false)}
        frameType={frameType}
        smaller={smaller}
      />
    </>
  );
};

PreviewBar.propTypes = {
  previewRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  renderDimensions: PropTypes.bool,
  dataId: PropTypes.string,
};

export default PreviewBar;
